import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { GET_CURRENT_USER_QUERY_KEY } from '@/hooks/api/users/useGetCurrentUser';
import { AuthService } from '../types';
import { useCompareCart } from '@/services/compareCart/useCompareCart';

export interface CognitoAuthService extends AuthService {
  login(): Promise<void>;
}

export const useCognitoAuth = (): CognitoAuthService => {
  const queryClient = useQueryClient();
  const cognitoAuth = useAuth();
  const { clearCart } = useCompareCart();

  const isAuthenticated = cognitoAuth.isAuthenticated && !cognitoAuth.isLoading;

  const login = async () => {
    await cognitoAuth.signinRedirect();
  };

  const logout = async () => {
    await cognitoAuth.removeUser();
    queryClient.resetQueries({ queryKey: [GET_CURRENT_USER_QUERY_KEY] });
    clearCart();

    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
    const logoutUri = import.meta.env.VITE_COGNITO_SIGNOUT_URI;
    const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN;

    globalThis.location.replace(
      `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`
    );
  };

  const getCurrentAccessToken = (): string | undefined => {
    return cognitoAuth.user?.access_token;
  };

  return {
    authType: 'cognito',
    isAuthenticated,
    isLoading: cognitoAuth.isLoading,
    login,
    logout,
    getCurrentAccessToken,
  };
};
